import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { adminApiClient } from "../../services/apiClient";
import { toApiError } from "../../types/api";

import type { AnalyticsPeriod, UserActivityReport } from "@lingoshade/domain";
import type { PayloadAction } from "@reduxjs/toolkit";

interface UserActivityState {
  report: UserActivityReport | null;
  days: AnalyticsPeriod;
  page: number;
  loading: boolean;
  error: string | null;
  requestId: string | null;
}

const initialState: UserActivityState = {
  report: null,
  days: 30,
  page: 1,
  loading: false,
  error: null,
  requestId: null,
};

export const fetchUserActivity = createAsyncThunk<
  UserActivityReport,
  { userId: string; days: AnalyticsPeriod; page: number },
  { rejectValue: string }
>(
  "userActivity/fetch",
  async (
    { userId, days, page },
    { rejectWithValue, signal },
  ): Promise<UserActivityReport | ReturnType<typeof rejectWithValue>> => {
    try {
      const response = await adminApiClient.get<UserActivityReport>(
        `/admin/users/${encodeURIComponent(userId)}/activity`,
        { params: { days, page }, signal },
      );
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(toApiError(error).message);
    }
  },
);

const slice = createSlice({
  name: "userActivity",
  initialState,
  reducers: {
    setUserActivityPeriod: (
      state,
      action: PayloadAction<AnalyticsPeriod>,
    ): void => {
      state.days = action.payload;
      state.page = 1;
    },
    setUserActivityPage: (state, action: PayloadAction<number>): void => {
      state.page = action.payload;
    },
    resetUserActivity: (): UserActivityState => initialState,
  },
  extraReducers: (builder): void => {
    builder
      .addCase(fetchUserActivity.pending, (state, action): void => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
        state.report = null;
      })
      .addCase(fetchUserActivity.fulfilled, (state, action): void => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        state.report = action.payload;
      })
      .addCase(fetchUserActivity.rejected, (state, action): void => {
        if (state.requestId !== action.meta.requestId) return;
        state.loading = false;
        if (!action.meta.aborted)
          state.error = action.payload ?? "REPORT_UNAVAILABLE";
      })
      .addMatcher(
        (action): boolean => action.type === "adminAuth/adminLogout",
        (): UserActivityState => initialState,
      );
  },
});

export const { setUserActivityPeriod, setUserActivityPage, resetUserActivity } =
  slice.actions;
export default slice.reducer;
